import { Draft } from '@reduxjs/toolkit';

// with optimistic updates, we have some items that are still speculated (did not get
// verification from server
// speculated messages will always come after verified messages, no other order
// is supported
export interface SpeculatedArray<A> {
  validated: Array<A>;
  speculated: Array<A>;
}

export function getAtOffset<A>(
  { validated, speculated }: SpeculatedArray<A>,
  offset: number | null | undefined,
): A | undefined {
  if (offset === null || offset === undefined) {
    return undefined;
  }
  if (offset < validated.length) {
    return validated[offset];
  } else {
    return speculated[offset - validated.length];
  }
}

export function emptySpeculatedArray<A>(): SpeculatedArray<A> {
  return {
    validated: [],
    speculated: [],
  };
}

export function lengthOf<A>(arr: SpeculatedArray<A> | undefined): number {
  if (arr === undefined) {
    return 0;
  }
  const { validated, speculated } = arr;
  return validated.length + speculated.length;
}

export function addAsSpeculated<A>(
  arr: Draft<SpeculatedArray<A>>,
  values: Array<A>,
): void {
  const vals = values as Array<Draft<A>>;
  arr.speculated.push(...vals);
}

export function addAsValidated<A>(
  arr: Draft<SpeculatedArray<A>>,
  values: Array<A>,
): void {
  const vals = values as Array<Draft<A>>;
  arr.validated.push(...vals);
}

export function moveFirstSpeculatedToValidated<A>(
  arr: Draft<SpeculatedArray<A>>,
): void {
  const firstSpeculated = arr.speculated.shift();
  if (firstSpeculated) {
    arr.validated.push(firstSpeculated);
  }
}

export function getFirstSpeculated<A>(arr: SpeculatedArray<A>): A | undefined {
  return arr.speculated[0];
}

export function hasSpeculated<A>(arr: SpeculatedArray<A>): boolean {
  return arr.speculated.length > 0;
}

export function numValidated<A>(arr: SpeculatedArray<A> | undefined): number {
  if (arr === undefined) {
    return 0;
  }
  return arr.validated.length;
}

export function numSpeculated<A>(arr: SpeculatedArray<A> | undefined): number {
  if (arr === undefined) {
    return 0;
  }
  return arr.speculated.length;
}

export function removeAllSpeculated<A>(
  arr: Draft<SpeculatedArray<A>> | undefined,
): void {
  if (arr !== undefined) {
    arr.speculated = [];
  }
}

export function lastOf<A>(arr: SpeculatedArray<A>): A {
  if (arr.speculated.length === 0) {
    return arr.validated[arr.validated.length - 1];
  } else {
    return arr.speculated[arr.speculated.length - 1];
  }
}

export function beforeLastOf<A>(arr: SpeculatedArray<A>): A {
  if (arr.speculated.length === 0) {
    return arr.validated[arr.validated.length - 2];
  } else if (arr.speculated.length === 1) {
    return arr.validated[arr.validated.length - 1];
  } else {
    return arr.speculated[arr.speculated.length - 2];
  }
}

export function firstOf<A>(arr: SpeculatedArray<A>): A {
  if (arr.validated.length === 0) {
    return arr.speculated[0];
  } else {
    return arr.validated[0];
  }
}

export function mkAsFullyValidated<A>(arr: Array<A>): SpeculatedArray<A> {
  return { validated: arr, speculated: [] };
}
